import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './SelectBox.module.css';
import { isOfType } from '../../utils/typeGuard';
import { StringByKey } from '../../types/common';
import { ListItem, SignupForm } from '../../types/login';
import { FeedbackForm } from '../../types/settings';
import {
  DEPARTMENT,
  FEEDBACK_TYPE,
  GRADE,
  MONTH,
  PAY_IN_FULL,
  REGION,
  SCHOOL_GRADE,
  SUBJECT,
  TEACHER,
  YEAR,
} from '../../constants';
import { ControllerFieldState, ControllerRenderProps } from 'react-hook-form';
import { MonthlyInstallments } from '../../types/membership';
import SimpleBar from 'simplebar-react';
import useSimpleBar from '../../hooks/useSimpleBar';

interface Select {
  name: string;
  list: ListItem[] | FeedbackForm[] | MonthlyInstallments[];
  setValue: (name: any, value: string) => void;
  curValue?: string | undefined;
  field?: ControllerRenderProps<SignupForm, any>;
  fieldState?: ControllerFieldState;
}

export default function SelectBox({
  name,
  list,
  setValue,
  curValue,
  field,
  fieldState,
}: Select) {
  const cx = classNames.bind(styles);
  const [isOpen, setIsOpen] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);

  const [simplebarRef] = useSimpleBar();

  const selectType: StringByKey = {
    teacherTypeCd: TEACHER,
    regionTypeCd: REGION,
    schoolTypeCd: SCHOOL_GRADE,
    gradeTypeCd: GRADE,
    subjectTypeCd: SUBJECT,
    departmentTypeCd: DEPARTMENT,
    qnaCd: FEEDBACK_TYPE,
    'paymentInfo.expMonth': MONTH,
    'paymentInfo.expYear': YEAR,
    'paymentInfo.cardQuota': PAY_IN_FULL,
  };
  const nm =
    list.filter((item) => item.cd === curValue)[0]?.nm ?? selectType[name];
  const [selectedValue, setSelectedValue] = useState(nm);

  const selectRef = useRef<HTMLDivElement>(null);

  const toggleItem = () => setIsOpen(!isOpen);

  const selectItem = (e: React.MouseEvent<HTMLLIElement>, code: string) => {
    const text = (e.target as HTMLLIElement).textContent || '';
    const value = code || '';

    setValue(name, value);
    setSelectedValue(text);
    setIsOpen(false);
    name !== 'monthly_installments' && setHasSelected(true);
  };

  useEffect(() => {
    const checkCloseSelect = ({ target }: MouseEvent) => {
      if (
        isOpen &&
        isOfType<HTMLElement>(target, 'nodeName') &&
        selectRef.current &&
        !selectRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener('mousedown', checkCloseSelect);

    return () => window.removeEventListener('mousedown', checkCloseSelect);
  }, [selectRef, isOpen]);

  useEffect(() => {
    setSelectedValue(nm);
  }, [nm]);

  return (
    <>
      <input {...field} type="text" className={cx('blind')} />
      <div
        className={cx(
          'select_wrap',
          { 'select--done': hasSelected && !name.includes('paymentInfo') },
          name
        )}
        ref={selectRef}
      >
        <button
          type="button"
          className={cx('btn', 'select_toggle')}
          onClick={toggleItem}
        >
          <span className={cx('setting_option')}>{selectedValue}</span>
        </button>
        {isOpen && (
          <div className={cx('select_menu')}>
            <SimpleBar forceVisible={true} ref={simplebarRef}>
              <ul className={cx('select_list')}>
                {list.map(({ cd, nm }) => (
                  <li
                    key={cd}
                    className="items"
                    onClick={(e) => selectItem(e, cd)}
                  >
                    <span>{nm}</span>
                  </li>
                ))}
              </ul>
            </SimpleBar>
          </div>
        )}
        {!name.includes('paymentInfo') && (
          <span className={cx('msg', 'invalid')}>
            {fieldState?.error?.message}
          </span>
        )}
      </div>
    </>
  );
}
